import React, { createContext, useContext, useEffect, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { convertAgentModelInfoToModelOption } from "src/agents/AgentVersionPage/AgentModelSelector";
import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import { useSequentialAutoSave } from "src/agents/AgentVersionPage/useSequentialAutoSave";
import { useAgentModels } from "src/agents/queries/management";
import { AgentVersion, AgentVersionConfigurationInput } from "src/agents/types";
import { AgentModelInfo } from "src/clients/agent-management";
import { ModelOption } from "src/genai/ModelSelector";

type AgentVersionFormProviderProps = {
  version: AgentVersion;
  children: React.ReactNode;
};

type AgentVersionFormContextValue = {
  modelOptions: ModelOption[];
  rawModels: AgentModelInfo[];
  isLoading: boolean;
};

const AgentVersionFormContext =
  createContext<AgentVersionFormContextValue | null>(null);

/**
 * Provides the form context for agent version configuration.
 * This provider wraps both the config panel and drawers so they can
 * share form state via useFormContext.
 *
 * Handles:
 * - Form creation with tool ids added
 * - Auto-save subscription to persist changes
 * - Model options fetching for the model selector
 */
export const AgentVersionFormProvider: React.FC<
  AgentVersionFormProviderProps
> = ({ version, children }) => {
  const formMethods = useForm<AgentVersionConfigurationInput>({
    defaultValues: version.configuration,
  });

  // Fetch available models for the model selector
  const modelsQuery = useAgentModels();

  // Filter out Anthropic models without a credential — they require BYOK
  // and are only relevant in the credential creation modal.
  const availableModels = useMemo(
    () =>
      (modelsQuery.data ?? []).filter(
        (model) => model.provider !== "anthropic" || !!model.credential_id,
      ),
    [modelsQuery.data],
  );

  const modelOptions = useMemo(
    () => availableModels.map(convertAgentModelInfoToModelOption),
    [availableModels],
  );

  const rawModels = availableModels;
  const isLoading = modelsQuery.isLoading;

  const isPublished = version.status === "published";
  const { ping: prewarmPing } = usePlaygroundContext();

  const { save } = useSequentialAutoSave({
    agentId: version.agent_id,
    versionId: version.id,
    name: version.name,
    description: version.description,
    initialEtag: version.etag,
    initialConfiguration: version.configuration,
    disabled: isPublished,
    onSave: prewarmPing,
  });

  // Subscribe to form changes and save changes to the server
  useEffect(() => {
    if (isPublished) return;

    const unsubscribe = formMethods.subscribe({
      formState: { values: true },
      callback: ({ values: configuration }) => save(configuration),
    });

    return () => {
      unsubscribe();
      save.flush(); // Flush debounce on unmount
    };
  }, [formMethods, isPublished, save]);

  return (
    <AgentVersionFormContext.Provider
      value={{ modelOptions, rawModels, isLoading }}
    >
      <FormProvider {...formMethods}>{children}</FormProvider>
    </AgentVersionFormContext.Provider>
  );
};

/**
 * Hook to access the agent version form context.
 * Provides model options and loading state.
 */
export const useAgentVersionFormContext = (): AgentVersionFormContextValue => {
  const context = useContext(AgentVersionFormContext);
  if (!context) {
    throw new Error(
      "useAgentVersionFormContext must be used within AgentVersionFormProvider",
    );
  }
  return context;
};
